<template>
  <div 
    class     = "controls-container pa-1" 
    v-hotkey  = "keymap"
  >
    <div class="modes px-4">

      <!-- Show Closed Captions -->
      <CaptionsButton
        :disabled   = "!video.canPlay || !hasCaptions"
      >
      </CaptionsButton>

      <!-- Overlay Timecode -->
      <VideoControlButton
        :disabled   = "!video.canPlay"
        :tooltip    = "video.showTimecodeOverlay ? 'Hide Timecode' : 'Overlay Timecode'"
        :color      = "video.showTimecodeOverlay ? 'primary' : 'default'"
        hotkey      = "t"
        @click      = "onToggleTimecodeOverlay()"
      >
        <v-icon>mdi-clock-outline</v-icon>
      </VideoControlButton>

      <!-- Playback Speed -->
      <div ref="playback_speed_mounting_point">
        <v-menu 
          top 
          offset-y 
          :attach   = "$refs.playback_speed_mounting_point"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              text 
              rounded 
              class     = "text-button px-1" 
              v-bind    = "attrs" 
              v-on      = "on" 
              :disabled = "!video.canPlay"
            >
              {{ video.isRewinding ? '-' : '' }}{{ video.playbackRate }}X
            </v-btn>
          </template>

          <v-list>
            <v-list-item @click="onPlaybackRateSelected(0.5)">0.5</v-list-item>
            <v-list-item @click="onPlaybackRateSelected(1)">1</v-list-item>
            <v-list-item @click="onPlaybackRateSelected(1.5)">1.5</v-list-item>
            <v-list-item @click="onPlaybackRateSelected(2)">2</v-list-item>
          </v-list>
        </v-menu>
      </div>

      <!-- Repeat -->
      <VideoControlButton
        :disabled   = "!video.canPlay"
        :tooltip    = "video.isOnRepeat ? 'Do Not Repeat' : 'Repeat'"
        :color      = "video.isOnRepeat ? 'primary' : 'default'"
        hotkey      = "r"
        @click      = "onToggleRepeat()"
      >
        <v-icon>mdi-repeat</v-icon>
      </VideoControlButton>

      <!-- Is Live -->
      <VideoControlButton
        v-if        = "video.isLive"
        :color      = "isAlmostLive ? 'error' : 'white'"
        :tooltip    = "isAlmostLive ? 'Live' : 'Jump To Live'"
        hotkey      = "g"
        @click      = "onJumpToLive();"
      >
        <v-icon>mdi-checkbox-blank-circle</v-icon>
      </VideoControlButton>

    </div>

    <div class="playback">

      <!-- Mark In -->
      <VideoControlButton 
        :disabled   = "!canSetInMarker" 
        tooltip     = "Mark In" 
        hotkey      = "i" 
        @click      = "onMarkIn()"
      >
        <span class="text-h6 pr-1">{</span>
      </VideoControlButton>

      <!-- Rewind -->
      <VideoControlButton 
        :disabled   = "!video.canPlay" 
        tooltip     = "Rewind" 
        hotkey      = "j" 
        @click      = "onRewind()"
      >
        <v-icon>mdi-rewind</v-icon>
      </VideoControlButton>

      <!-- Jump to Beginning -->
      <VideoControlButton
        :disabled   = "!video.canPlay"
        tooltip     = "Jump to Beginning"
        hotkey      = "Home"
        @click      = "onJumpToBeginning()"
      >
        <v-icon>mdi-page-first</v-icon>
      </VideoControlButton>

      <!-- Back One Frame -->
      <VideoControlButton 
        :disabled   = "!video.canPlay" 
        tooltip     = "Jump Back One Frame" 
        hotkey      = "←" 
        @click      = "onOneFrameBack()"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </VideoControlButton>

      <!-- Play -->
      <VideoControlButton
        v-if        = "video.paused && !video.isRewinding"
        :disabled   = "!video.canPlay"
        tooltip     = "Play"
        hotkey      = "Space"
        large
        @click      = "onPlay()"
      >
        <v-icon>mdi-play</v-icon>
      </VideoControlButton>

      <!-- Paused -->
      <VideoControlButton 
        v-else 
        :disabled   = "!video.canPlay" 
        tooltip     = "Pause" 
        hotkey      = "Space" 
        large 
        @click      = "onPause()"
      >
        <v-icon>mdi-pause</v-icon>
      </VideoControlButton>

      <!-- Forward One Frame -->
      <VideoControlButton
        :disabled   = "!video.canPlay"
        tooltip     = "Jump Forward One Frame"
        hotkey      = "→"
        @click      = "onOneFrameForward()"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </VideoControlButton>

      <!-- Jump to End -->
      <VideoControlButton 
        :disabled   = "!video.canPlay"
        :tooltip    = "isLastSegmentBuffered ? 'Jump to End' : 'Jump to End is not possible before the end of the video is buffered'"
        :hotkey     = "isLastSegmentBuffered ? 'End' : ''"
        :color      = "isLastSegmentBuffered ? 'white' : 'rgba(255, 255, 255, 0.3)'"
        @click      = "onJumpToEnd()"
      >
        <v-icon>mdi-page-last</v-icon>
      </VideoControlButton>

      <!-- Fast-Forward -->
      <VideoControlButton 
        :disabled   = "!video.canPlay" 
        tooltip     = "Fast-Forward" 
        hotkey      = "l" 
        @click      = "onFastForward()"
      >
        <v-icon>mdi-fast-forward</v-icon>
      </VideoControlButton>

      <!-- Mark Out -->
      <VideoControlButton 
        :disabled   = "!canSetOutMarker" 
        tooltip     = "Mark Out" 
        hotkey      = "o" 
        @click      = "onMarkOut()"
      >
        <span class="text-h6">}</span>
      </VideoControlButton>
    </div>

    <div class="settings px-4 py-0">
      <VideoControlButton
        v-if          = "canShowVolume"
        :disabled     = "!video.canPlay"
        :tooltip      = "video.isMuted ? 'Unmute' : 'Mute'"
        hotkey        = "n"
        @click        = "onToggleMute()"
      >
        <v-icon>{{ video.isMuted ? 'mdi-volume-off' : 'mdi-volume-high' }}</v-icon>
      </VideoControlButton>

      <!-- Volume -->
      <v-slider
        v-if          = "canShowVolume"
        class         = "volume-slider"
        hide-details
        min           = "0"
        max           = "1"
        step          = "0.01"
        color         = "white"
        :disabled     = "!canAdjustVolume"
        :value        = "video.volume"
        @input        = "onVolumeAdjustment($event)"
      >
      </v-slider>

      <!-- Settings -->
      <VideoSettings></VideoSettings>

      <!-- Fullscreen -->
      <VideoControlButton
        :disabled     = "!video.canPlay"
        :tooltip      = "isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'"
        hotkey        = "f"
        @click        = "onToggleFullscreen()"
      >
        <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
      </VideoControlButton>
    </div>
  </div>
</template>

<script lang="js">

// #region Imports

/* vuex */
import {mapState} from 'vuex';

/* Telestream */
import {
  CLEAR_MARK_IN,
  CLEAR_MARK_OUT,
  FAST_FORWARD,
  JUMP_BACK_FRAMES,
  JUMP_FORWARD_FRAMES,
  JUMP_TO_BEGINNING,
  JUMP_TO_END,
  JUMP_TO_TIME,
  JUMP_TO_PREVIOUS_MARKER,
  JUMP_TO_NEXT_MARKER,
  JUMP_TO_PREVIOUS_IFRAME,
  JUMP_TO_NEXT_IFRAME,
  LOG_CLIP,
  PLAY_VIDEO,
  PAUSE_VIDEO,
  REWIND,
  MARK_IN,
  MARK_OUT,
  CHANGE_PLAYBACK_RATE,
  SELECT_NEXT_AUDIO_TRACK,
  SELECT_NEXT_SUBTITLE_SOURCE,
  TOGGLE_MUTE,
  TOGGLE_REPEAT,
  ADJUST_VOLUME,
  SHOW_FULLSCREEN,
  EXIT_FULLSCREEN,
  SELECT_PERSISTED_SUBTITLE_SOURCE
} from '@/utils/action-types';
import {
  SET_SHOW_TIMECODE_OVERLAY
} from '@/utils/mutation-types'
import VideoSettings from './VideoSettings';
import CaptionsButton from './buttons/CaptionsButton';
import VideoControlButton from './buttons/VideoControlButton';

// #endregion Imports

// #region Constants

/*
 * Since live media takes a bit to buffer, the playhead can't just stick to the
 * duration of the clip. The value below is distance (in seconds) from the end
 * of the media we consider to be "live".
 */
const LIVE_OFFSET_FROM_END = 10;

// #endregion Constants

export default {

  // #region Properties

  name:     'VideoControls',
  computed: {
    ...mapState([
      'videoController',
      'video',
      'indexer'
    ]),
    hasCaptions() {
      if (!this.videoController) {
        return false;
      }

      return (
        (this.videoController.sidecarSubtitles.length > 0) ||
        (this.videoController.availableServices.length > 0)
      );
    },
    isAlmostLive() {
      return this.video.currentTime > (this.video.duration - LIVE_OFFSET_FROM_END);
    },
    canSetInMarker() {
      return this.video.canPlay;
    },
    canSetOutMarker() {
      return this.video.canPlay;
    },
    canShowVolume() {
      // Can't set volume on Safari Mobile: https://stackoverflow.com/questions/2602541/html5-video-volume
      const isSafariMobile = (navigator.userAgent.indexOf('Safari') > -1)
          && (navigator.userAgent.indexOf('Chrome') === -1)
          && window.outerWidth <= 1200;

      return !isSafariMobile;
    },
    canAdjustVolume() {
      return this.video.canPlay && !this.video.isMuted;
    },
    isFullscreen() {
      return this.video.isFullscreen;
    },
    isLastSegmentBuffered (){
      return this.videoController && this.videoController.isLastSegmentBuffered;
    },
    keymap () {
      return {
        'space': () => {
          this.$store.dispatch(CHANGE_PLAYBACK_RATE, 1);
          this.onTogglePlayback();
        },
        'left':        () => this.onOneFrameBack(),
        'shift+left':  () => this.$store.dispatch(JUMP_BACK_FRAMES, 10),
        'ctrl+left':   () => this.$store.dispatch(JUMP_TO_TIME, this.video.currentTime - 10),
        'right':       () => this.onOneFrameForward(),
        'shift+right': () => this.$store.dispatch(JUMP_FORWARD_FRAMES, 10),
        'ctrl+right':  () => this.$store.dispatch(JUMP_TO_TIME, this.video.currentTime + 10),
        'k':           () => this.onPause(),
        'j':           () => this.onRewind(),
        'l':           () => this.onFastForward(),
        'i':           () => this.onMarkIn(),
        'shift+i':     () => {
          if (this.video.inMarker !== undefined) {
            this.$store.dispatch(JUMP_TO_TIME, this.video.inMarker)
          }
        },
        'ctrl+shift+i': () => this.$store.dispatch(CLEAR_MARK_IN),
        'o':            () => this.onMarkOut(),
        'shift+o':      () => {
          if (this.video.outMarker !== undefined) {
            this.$store.dispatch(JUMP_TO_TIME, this.video.outMarker)
          }
        },
        'ctrl+shift+o': () => this.$store.dispatch(CLEAR_MARK_OUT),
        'p':            () => this.$store.dispatch(LOG_CLIP),
        'c':            () => this.onNextSubtitleTrack(),
        'r':            () => this.onToggleRepeat(),
        'n':            () => this.onToggleMute(),
        'ctrl+a':       () => this.onNextAudioTrack(),
        't':            () => this.onToggleTimecodeOverlay(),
        'f':            () => this.onToggleFullscreen(),
        'home':         () => this.onJumpToBeginning(),
        'end':          () => this.onJumpToEnd(),
        '1':            () => this.$store.dispatch(JUMP_TO_TIME, this.video.duration * 0.1),
        '2':            () => this.$store.dispatch(JUMP_TO_TIME, this.video.duration * 0.2),
        '3':            () => this.$store.dispatch(JUMP_TO_TIME, this.video.duration * 0.3),
        '4':            () => this.$store.dispatch(JUMP_TO_TIME, this.video.duration * 0.4),
        '5':            () => this.$store.dispatch(JUMP_TO_TIME, this.video.duration * 0.5),
        '6':            () => this.$store.dispatch(JUMP_TO_TIME, this.video.duration * 0.6),
        '7':            () => this.$store.dispatch(JUMP_TO_TIME, this.video.duration * 0.7),
        '8':            () => this.$store.dispatch(JUMP_TO_TIME, this.video.duration * 0.8),
        '9':            () => this.$store.dispatch(JUMP_TO_TIME, this.video.duration * 0.9),
        'g':            () => this.onJumpToLive(),
        '[':            () => this.$store.dispatch(JUMP_TO_PREVIOUS_MARKER),
        ']':            () => this.$store.dispatch(JUMP_TO_NEXT_MARKER),
        'shift+[':      () => this.$store.dispatch(JUMP_TO_PREVIOUS_IFRAME),
        'shift+]':      () => this.$store.dispatch(JUMP_TO_NEXT_IFRAME)
      };
    }
  },
  components: {
    CaptionsButton,
    VideoControlButton,
    VideoSettings
  },

  // #endregion Properties

  // #region Events

  /*
   * This is a bit of a work-around around the fact that there is no way to tell
   * when all subtitles have been found in the media.
   */
  watch: {
    ['videoController.availableServices']() {
      if (this.videoController && (this.videoController.currentSubtitles === 'Off')) {
        this.$store.dispatch(SELECT_PERSISTED_SUBTITLE_SOURCE);
      }
    },
  },

  methods: {

    onTogglePlayback() {
      if (this.video.paused && !this.video.isRewinding) {
        this.$store.dispatch(PLAY_VIDEO);
      } else {
        this.$store.dispatch(PAUSE_VIDEO);
      }
    },

    onPlay() {
      this.$store.dispatch(PLAY_VIDEO);
    },

    onPause() {
      this.$store.dispatch(PAUSE_VIDEO);
    },

    onRewind() {
      this.$store.dispatch(REWIND);
    },

    onFastForward() {
      this.$store.dispatch(FAST_FORWARD);
    },

    onOneFrameBack() {
      this.$store.dispatch(JUMP_BACK_FRAMES, 1);
    },

    onOneFrameForward() {
      this.$store.dispatch(JUMP_FORWARD_FRAMES, 1);
    },

    onJumpToBeginning() {
      this.$store.dispatch(JUMP_TO_BEGINNING);
    },

    onJumpToEnd() {
      if (this.video.canPlay && this.videoController.isLastSegmentBuffered){
        this.$store.dispatch(JUMP_TO_END);
      }
    },

    onJumpToLive() {
      this.$store.dispatch(JUMP_TO_TIME, this.video.duration - (LIVE_OFFSET_FROM_END / 2));
      this.$store.dispatch(PLAY_VIDEO);
    },

    onToggleTimecodeOverlay() {
      this.$store.commit(SET_SHOW_TIMECODE_OVERLAY, !this.video.showTimecodeOverlay);
    },

    onToggleRepeat() {
      this.$store.dispatch(TOGGLE_REPEAT);
    },

    onPlaybackRateSelected(rate) {
      this.$store.dispatch(CHANGE_PLAYBACK_RATE, rate);
    },

    onToggleMute() {
      this.$store.dispatch(TOGGLE_MUTE);
    },

    onNextAudioTrack() {
      this.$store.dispatch(SELECT_NEXT_AUDIO_TRACK);
    },

    onNextSubtitleTrack() {
      this.$store.dispatch(SELECT_NEXT_SUBTITLE_SOURCE);
    },

    onVolumeAdjustment(volume) {
      this.$store.dispatch(ADJUST_VOLUME, volume);
    },

    onToggleFullscreen() {
      if (this.isFullscreen) {
        this.$store.dispatch(EXIT_FULLSCREEN);
      } else {
        this.$store.dispatch(SHOW_FULLSCREEN);
      }
    },

    onMarkIn() {
      this.$store.dispatch(MARK_IN);
    },

    onMarkOut() {
      this.$store.dispatch(MARK_OUT);
    }

  }

  // #endregion Events

}
</script>

<style scoped lang="scss">

.controls-container {
  display:                grid;
  grid-template-columns:  1fr auto 1fr;
  align-items:            center;

  .modes,
  .settings {
    display:    flex;
    flex-wrap:  wrap;
  }

  .settings {
    justify-content: flex-end;
  }

  .volume-slider {
    width:      75px;
    max-width:  75px;
  }

}

.text-button {
  min-width: 48px !important;
}
</style>
